// shared-lib/cabinet-runs/ownership.js
//
// The appliance object a derived run's bay anchors carries that bay's id
// (`bayId`). That is what the 2D bay drag keys on (`object.bayId` → the run
// re-solves and moves the appliance with its group along the run) — without
// it an applied kitchen's sink was a free object until the first re-solve
// stamped it, and "Move Group" carried sink + cabinet + countertop off the
// wall (found 2026-08-30 on the first browser test).
//
// Deliberately NOT `runId`: on the scan path the run RESERVES a measured
// appliance and the scan owns it (`deriveScanRuns` skips `runId`-tagged
// objects on re-derive, so tagging would drop the reservation on reprocess),
// and on the auto path a re-solve discards run-owned objects and re-emits
// bays — a layout's sink basin is emitted by the generator, not the fill.

const { baysForRun, isApplianceKind } = require('./bays');
const { runAliases } = require('./runIds');
const { isDerivedRun } = require('./runSource');
const {
  toRunApplianceTypeForObject,
} = require('../kitchen-assembly/cabinetLayout/wallToRun');

/**
 * `objectId → bayId` for every APPLIANCE bay in `runs`, and the runs whose
 * bays could not be read at all. ONE index, because the merge runs the stamp
 * and then the contract back to back over the same objects: a second copy of
 * this loop that drifted would have the contract report a
 * `bay-names-object-without-bayId` violation about the object the stamp had
 * just fixed, and that warning is persisted.
 *
 * A run it cannot read is SKIPPED and named, never thrown from. `baysForRun`
 * migrates a legacy `appliances[]` entry through `applianceBayFromLegacy`,
 * which throws for a type outside `APPLIANCE_KIND_BY_RUN_TYPE` — `wall-oven`
 * is one, present in `wallToRun.APPLIANCE_TYPE_MAP` but with no legacy bay
 * kind. The contract warns and never throws, and a stored run nobody can read
 * must not abort the edit that happened to sweep past it.
 *
 * @param {Object[]} runs stored runs (bays or legacy appliances[])
 * @returns {{ byObjectId: Map<string,string>, unreadable: Array<{id, message}> }}
 */
function applianceBayIdByObjectId(runs) {
  const byObjectId = new Map();
  const unreadable = [];
  for (const run of runs || []) {
    let bays;
    try {
      bays = baysForRun(run);
    } catch (err) {
      unreadable.push({ id: run?.id, message: err.message });
      continue;
    }
    for (const bay of bays) {
      if (!isApplianceKind(bay.kind)) continue;
      for (const id of [bay.id, bay.applianceId]) {
        if (id && !byObjectId.has(id)) byObjectId.set(id, bay.id);
      }
      // Every section of an oven column, AND the appliance it houses, answers
      // to the one bay — the sections so the 2D bay drag and the panel find
      // the column from any of the three, the oven so `stampApplianceBays`
      // can heal a `bayId` onto it at all (the run only POSITIONS an oven —
      // `bayId`, never `runId`, see `incumbents.emitOvenColumns` — but a
      // `bayId` this index never learned to stamp is a `bayId` the drag can
      // never use either).
      for (const id of [...(bay.column?.partIds || []), bay.column?.ovenId]) {
        if (id && !byObjectId.has(id)) byObjectId.set(id, bay.id);
      }
    }
  }
  return { byObjectId, unreadable };
}

/**
 * Pure. Never clobbers an existing `bayId`; identity when nothing changes.
 * @param {Object[]} objects
 * @param {Object[]} runs stored runs (bays or legacy appliances[])
 */
function stampApplianceBays(objects, runs) {
  const { byObjectId: bayIdByObjectId } = applianceBayIdByObjectId(runs);
  if (!bayIdByObjectId.size) return objects;
  let changed = false;
  const out = (objects || []).map((o) => {
    const bayId = o?.id ? bayIdByObjectId.get(o.id) : null;
    if (!bayId || o.bayId) return o;
    changed = true;
    return { ...o, bayId };
  });
  return changed ? out : objects;
}

/**
 * The runs to carry forward after a layout APPLY has replaced a room's
 * `objects` wholesale — a kitchen layout or a bathroom layout.
 *
 * A reprocess used to call this too, with `'scan'`. It no longer does: a
 * reprocess rebuilds every room document from the scan and keeps no run at
 * all, drawn or derived (user ruling 2026-10-23), so it has nothing to carry.
 *
 * Both callers used to ask only "did I produce this run?" (`source !==` their
 * own tag) and keep everything else. That is the wrong question, because the
 * two halves of a replace disagree about what "the old kitchen" is: the OBJECT
 * sweep is geometric (`mergeKitchenObjects` demolishes what the kitchen polygon
 * owns and preserves the rest), while the run filter was by `source`. So an
 * apply swept a scan run's objects and kept its spec, and a reprocess replaced
 * an auto run's objects and kept its spec — leaving a run that owns nothing.
 *
 * A zero-owner run is not inert. `nearestRunSegment` iterates runs in order
 * with a strict `<`, so a stale run listed first WINS an appliance drop on a
 * wall it shares and re-emits its whole superseded spec; room-scope appearance
 * re-materializes it; `runEndsForRoom` offers its ends as link targets.
 *
 * The question both halves actually share is ownership: does anything that
 * SURVIVED still carry this run's id? That keeps a run the sweep spared (an
 * unclaimed run outside the kitchen area, whose members `mergeKitchenObjects`
 * deliberately preserves whole) and drops one whose objects are gone.
 *
 * With ONE exemption: a run the user DREW (no `source`) is authored, not
 * regenerable output, and is kept whether it currently owns anything or not.
 * A derived run that owns nothing can always be re-derived from the scan or
 * re-generated; a hand-drawn one cannot be got back at all, so the ownership
 * test must never be the thing that deletes it. The client guards are scoped
 * the same way (`isStaleDerivedRun` in the web `cabinetRunGeometry`): a kept
 * hand-drawn run must stay reachable, and a room-scope restyle re-materializing
 * it is how it comes back once an apply has swept its cabinets.
 *
 * @param {Object[]} existingRuns the room's runs before the write
 * @param {Object[]} survivingObjects the objects being written
 * @param {string} replacedSource the `source` this producer emits and is
 *   replacing wholesale ('auto' for an apply)
 * @returns {Object[]} the runs to keep, in their original order
 */
function runsToCarryForward(existingRuns, survivingObjects, replacedSource) {
  const owned = new Set();
  for (const o of survivingObjects || []) {
    if (o?.runId) owned.add(o.runId);
  }
  return (existingRuns || []).filter((r) => {
    if (!r?.id || r.source === replacedSource) return false;
    return !r.source || owned.has(r.id);
  });
}

/**
 * The room's runs once a layout is written: the stored runs carried forward
 * (`runsToCarryForward`), then the layout's own. A run the layout states AGAIN
 * is saved once, as the layout states it — option #1 carries the room's
 * island and peninsula runs through the apply (`_carriedRuns`), and a carried
 * SCAN run's objects survive the merge under its id, so carrying forward by
 * ownership would keep the stored copy beside the restated one.
 *
 * @param {Object[]} existingRuns the room's runs before the write
 * @param {Object[]} survivingObjects the objects being written
 * @param {string} replacedSource see `runsToCarryForward`
 * @param {Object[]} layoutRuns the runs the layout brings
 * @returns {Object[]}
 */
function runsAfterApply(
  existingRuns,
  survivingObjects,
  replacedSource,
  layoutRuns = []
) {
  const restated = new Set((layoutRuns || []).map((r) => r?.id));
  return [
    ...runsToCarryForward(
      existingRuns,
      survivingObjects,
      replacedSource
    ).filter((r) => !restated.has(r.id)),
    ...(layoutRuns || []),
  ];
}

/**
 * A run no object in `objects` claims. Whether that makes it STALE depends on
 * its `source` — a derived run owning nothing is a leftover spec, a hand-drawn
 * one is an authored run waiting to be rebuilt — so callers combine this with
 * the run's source before acting on it (web: `isStaleDerivedRun`).
 */
function runOwnsNoObjects(run, objects) {
  if (!run?.id) return true;
  return !(objects || []).some((o) => o?.runId === run.id);
}

/**
 * The ownership contract (proposal §6), checked rather than trusted:
 *   - `runId` means the run EMITS the object. An appliance is never emitted
 *     by a derived run (auto / scan) — it is positioned (`bayId`) — so a
 *     `runId` on one is a violation. A hand-drawn run emits its appliances
 *     through `buildApplianceObjects` and stamps both; that is the design.
 *   - an object a bay names carries that bay's id (`bayId`), or the 2D bay
 *     drag is dark on it (the 47-object gap of 2026-09-05).
 *   - a `runId` names a run that exists, by id or per-wall alias.
 *   - a stored run can be READ (`run-unreadable`): a legacy `appliances[]`
 *     entry of a type with no bay kind makes `baysForRun` throw, and nothing
 *     downstream can see that run's bays — reported, so the merge neither
 *     throws nor stays silent about the ones it skipped.
 * Pure; reports, never repairs (`stampApplianceBays` repairs the second).
 *
 * @param {Object[]} objects
 * @param {Object[]} runs
 * @returns {Array<{ id, type, rule, runId?, bayId?, message? }>}
 */
function ownershipViolations(objects, runs) {
  const runById = new Map();
  // An unreadable run still EXISTS — only its bays are out of reach — so it
  // must answer for its own id here or every object it owns reads as a tag
  // naming no run.
  for (const r of runs || []) {
    if (!r?.id) continue;
    runById.set(r.id, r);
    for (const a of runAliases(r)) runById.set(a, r);
  }
  const { byObjectId: bayIdByObjectId, unreadable } =
    applianceBayIdByObjectId(runs);
  const out = unreadable.map((u) => ({
    id: u.id,
    type: 'run',
    rule: 'run-unreadable',
    message: u.message,
  }));
  for (const o of objects || []) {
    if (!o?.id) continue;
    if (o.runId && !runById.has(o.runId)) {
      out.push({
        id: o.id,
        type: o.type,
        rule: 'runId-names-no-run',
        runId: o.runId,
      });
    }
    const owner = o.runId ? runById.get(o.runId) : null;
    if (isDerivedRun(owner) && toRunApplianceTypeForObject(o)) {
      out.push({
        id: o.id,
        type: o.type,
        rule: 'appliance-emitted-by-derived-run',
        runId: o.runId,
      });
    }
    const bayId = bayIdByObjectId.get(o.id);
    if (bayId && !o.bayId) {
      out.push({
        id: o.id,
        type: o.type,
        rule: 'bay-names-object-without-bayId',
        bayId,
      });
    }
  }
  return out;
}

/** An upper-tier cabinet: the run's uppers and the upper half of a corner. */
function isUpperTier(o) {
  return (
    o?.type === 'upper-cabinet' ||
    (o?.type === 'corner-cabinet' && o?.dimensions?.isUpper === true)
  );
}

/**
 * Is `obj` a box the run ENGINE built — a fill's cabinet, filler, corner or
 * install piece — as against cabinetry a run only claims (a scanned box, the
 * builder's own vanity cabinet before its first fill)? Read off the fill's own
 * provenance stamp, with the run id that says whose it is.
 *
 * A tool that treats a vanity as ONE cabinet in a fixture group asks this:
 * a box the run built is the run's to rebuild, never a fixture of its own
 * (a vanity built in a cabinet line is filled at once, 2026-10-21).
 */
function isRunFilledBox(obj) {
  return Boolean(obj?.runId) && obj.addedBy === 'manual-cabinet-run';
}

module.exports = {
  isRunFilledBox,
  applianceBayIdByObjectId,
  stampApplianceBays,
  ownershipViolations,
  runsToCarryForward,
  runsAfterApply,
  runOwnsNoObjects,
  isUpperTier,
};
